import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { Separator } from '@voll-intelligence/ui-kit/ui/separator';
import { scrollParentOf } from './scroll_parent.js';

// A coluna de cortes do `ResultsLayout` na linha (o contrato está no cabeçalho de
// `results_layout.tsx`): o painel sticky, o corpo que rola e o rodapé de vidro.

/**
 * A coluna cabe na viewport TAMBÉM no topo da página. A classe `max-h-[calc(100dvh − chrome −
 * 1.5rem)]` só acerta DEPOIS que o `sticky top-6` grudou (topo = chrome + 1.5rem); no topo da
 * página a coluna nasce abaixo da faixa de busca e seu pé — onde vive o composer do filtro por
 * texto de voo — ficava fora da tela até rolar. Aqui medimos o topo NATURAL da coluna (o do pai
 * imediato, que não é sticky — medir o próprio `aside` realimenta: o sticky o reposiciona em
 * função da altura dele) e publicamos `--hui-filters-fit = viewport − topo`; a classe toma o
 * `min()` disso com a fórmula de sempre, então grudada a fórmula vence sozinha e antes disso a
 * coluna encolhe para caber. Sem JS a variável cai no fallback `100dvh` e nada muda. Só
 * com o pino (`listScroll: 'host'`): esticada na linha travada, não há o que medir.
 */
/** O `top-6` do pino da coluna (e o `pt-6` da linha), em px. */
const PIN_TOP_PX = 24;

function useViewportFit(ref: RefObject<HTMLElement | null>, enabled: boolean): number | undefined {
  const [fit, setFit] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return undefined;
    const anchor = el.parentElement ?? el;
    // O pé disponível é o do SCROLLER, não o da janela: o explorer de voos rola dentro de
    // `.fex-results` e tem um rodapé abaixo dele — medindo até a viewport, o card do composer
    // descia por trás do rodapé (cortado, e coberto pelo "atualizando a lista…").
    const scroller = scrollParentOf(anchor);
    let frame = 0;
    const measure = (): void => {
      frame = 0;
      const port = scroller?.getBoundingClientRect();
      // Pinada, a coluna vive a `PIN_TOP_PX` do topo do SCROLLER — não da janela: o
      // explorer de voos rola dentro de `.fex-results`, abaixo da barra do host, e sem
      // isto a fórmula em CSS (`100dvh − chrome`) contava a barra como espaço da coluna
      // quando o host não publica `--hui-results-page-chrome`.
      const top = Math.max(anchor.getBoundingClientRect().top, (port?.top ?? 0) + PIN_TOP_PX);
      const bottom = port ? Math.min(window.innerHeight, port.bottom) : window.innerHeight;
      // Sem piso: qualquer piso acima do espaço disponível devolve o pé da coluna para fora da
      // viewport (o bug que isto corrige) — numa viewport baixa a coluna fica curta e rolável.
      const next = Math.max(0, Math.round(bottom - top));
      setFit((prev) => (prev === next ? prev : next));
    };
    const schedule = (): void => {
      if (frame === 0) frame = requestAnimationFrame(measure);
    };
    measure();
    // `scroll` não borbulha, mas captura-se no documento: vale para o scroller do host
    // (`.chat-scroll`) e para a janela, sem a casca conhecer nenhum dos dois.
    document.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', schedule);
    // O host pode montar a coluna escondida (a bancada segura o explorer sob `hidden` até a
    // busca assentar): com `display:none` o topo mede zero e a medida sai com a viewport
    // inteira — e nem scroll nem resize avisam quando o `hidden` cai. O tamanho do próprio
    // aside (0 → real) é o sinal; a re-medida devolve o mesmo valor quando nada mudou.
    const observer =
      typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(schedule);
    observer?.observe(el);
    // O scroller também: o rodapé do host aparece e some sem scroll nem resize, e o pé
    // disponível muda com ele.
    if (scroller) observer?.observe(scroller);
    return () => {
      if (frame !== 0) cancelAnimationFrame(frame);
      document.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      observer?.disconnect();
    };
  }, [ref, enabled]);
  return fit;
}

/** A altura do nó, acompanhando o que muda dentro dele (as marcas de filtro que
 *  o composer ganha e perde). Zero sem nó. */
function useHeightOf(node: HTMLElement | null): number {
  const [height, setHeight] = useState(0);
  useLayoutEffect(() => {
    if (!node) return undefined;
    const measure = (): void => {
      const next = Math.round(node.getBoundingClientRect().height);
      setHeight((prev) => (prev === next ? prev : next));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [node]);
  // Without a node there is nothing to measure; the next node is measured before paint.
  return node ? height : 0;
}

/** O painel pinado no scroller do host (`listScroll: 'host'`), com a altura medida acima. */
const PINNED_CLASS =
  'sticky top-6 flex max-h-[min(calc(100dvh_-_var(--hui-results-page-chrome,0px)_-_3rem),calc(var(--hui-filters-fit,100dvh)_-_1.5rem))] w-72 shrink-0 flex-col gap-3 self-start pt-2';
/** O painel esticado na linha de altura travada (`listScroll: 'own'`): o host não rola,
 *  então não há pino nem medida — o pé fica a 1.5rem do pé do host, o mesmo respiro. O
 *  `relative` é o que o `sticky` dava ao rodapé absoluto. */
const FILLED_CLASS = 'relative mb-6 flex min-h-0 w-72 shrink-0 flex-col gap-3 pt-2';

export function FiltersColumn({
  copy,
  canReset,
  onReset,
  footer,
  fill,
  children,
}: {
  readonly copy: { readonly filtersLabel: string; readonly resetFiltersLabel: string };
  readonly canReset: boolean;
  readonly onReset: () => void;
  readonly footer: ReactNode;
  /** A linha tem altura travada (`listScroll: 'own'`): estica em vez de pinar. */
  readonly fill: boolean;
  readonly children: ReactNode;
}): ReactNode {
  const asideRef = useRef<HTMLElement>(null);
  const fit = useViewportFit(asideRef, !fill);
  const [footerNode, setFooterNode] = useState<HTMLDivElement | null>(null);
  const footerHeight = useHeightOf(footerNode);
  return (
    <aside
      ref={asideRef}
      aria-label={copy.filtersLabel}
      className={fill ? FILLED_CLASS : PINNED_CLASS}
      style={
        fill || fit === undefined
          ? undefined
          : ({ '--hui-filters-fit': `${String(fit)}px` } as CSSProperties)
      }
    >
      <div className="flex h-8 items-center justify-between">
        <h2 className="m-0 text-[15px] leading-[22px] font-semibold">{copy.filtersLabel}</h2>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-muted-foreground -mr-2"
          disabled={!canReset}
          onClick={onReset}
        >
          {copy.resetFiltersLabel}
        </Button>
      </div>
      <Separator />
      {/* O `-mx-2 px-2` é um respiro que não move nada: o hover dos checkboxes (-4px)
          e os polegares do slider (7px) pendem para fora da caixa do eixo, e sem ele o
          `overflow-y: auto` (que força `overflow-x: auto`) vira barra horizontal. A
          margem da direita leva também a calha da `scrollbar-rail`, que cai no vão até
          a lista (`COLUMN_GAP_PX`) em vez de estreitar os eixos. O `padding-bottom` é a
          altura do rodapé: no fim da rolagem o último eixo para inteiro acima dele. */}
      <div
        className="scrollbar-rail -ml-2 mr-[calc(-0.5rem_-_var(--scrollbar-rail))] flex min-h-0 flex-1 flex-col overflow-y-auto px-2"
        style={footerHeight > 0 ? { paddingBottom: footerHeight } : undefined}
      >
        {children}
      </div>
      {footer != null && (
        // Por cima do pé do corpo, FORA dele: dentro, o recorte do scroller cortava a
        // sombra do card num retângulo. O `pt-5` é a faixa em que o desfoque nasce acima
        // do card, e o `-mx-2 px-2` estende o vidro sobre o respiro do corpo, sem orelhas.
        // O vidro passa 2rem das bordas dentro de um recorte do tamanho do rodapé: o
        // Chrome escurece a borda de um `backdrop-filter: blur`, e solto ele desenhava um
        // retângulo cinza em volta do card (o topo some no degradê da `glass-bottom`).
        <div ref={setFooterNode} className="absolute inset-x-0 bottom-0 z-10 -mx-2 px-2 pt-5">
          <div aria-hidden="true" className="absolute inset-0 -z-10 overflow-hidden">
            <div className="glass-bottom absolute -inset-x-8 top-0 -bottom-8" />
          </div>
          {footer}
        </div>
      )}
    </aside>
  );
}
